import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import usePageMeta from "../utils/usePageMeta";
import { fetchUser, logout } from "../utils/auth";
import { SITE_NAME } from "../utils/site";

const Profile = () => {
  usePageMeta({
    title: "Your profile",
    description: `Your ${SITE_NAME} account and the posts you have written.`,
    path: "/profile",
  });

  const [state, setState] = useState({ status: "loading", user: null, posts: [] });

  const load = async () => {
    const user = await fetchUser();
    if (!user) return setState({ status: "signed-out", user: null, posts: [] });

    try {
      const res = await fetch("/api/posts");
      const all = res.ok ? await res.json() : [];
      const posts = Array.isArray(all)
        ? all.filter((p) => p.username === user.username)
        : [];
      setState({ status: "ready", user, posts });
    } catch {
      setState({ status: "ready", user, posts: [] });
    }
  };

  useEffect(() => {
    load();
  }, []);

  if (state.status === "loading") {
    return (
      <div className="page profile">
        <h1>Your profile</h1>
        <p className="chart-sub">Loading…</p>
      </div>
    );
  }

  if (state.status === "signed-out") {
    return (
      <div className="page profile">
        <h1>Your profile</h1>
        <p>
          You are not signed in. Create an account or sign in from the{" "}
          <Link to="/community">community page</Link> to post, comment, and see
          your activity here.
        </p>
      </div>
    );
  }

  const { user, posts } = state;
  const comments = posts.reduce((n, p) => n + (p.comments?.length || 0), 0);
  const likes = posts.reduce((n, p) => n + (p.likes?.length || 0), 0);

  return (
    <div className="page profile">
      <h1>Your profile</h1>

      <p className="signed-in-as">
        Signed in as <strong>@{user.username}</strong>{" "}
        <button
          className="logout-btn"
          onClick={async () => {
            await logout();
            setState({ status: "signed-out", user: null, posts: [] });
          }}
        >
          Log out
        </button>
      </p>

      <div className="profile-stats">
        <div className="stat-tile">
          <span className="stat-value">{posts.length}</span>
          <span className="stat-label">{posts.length === 1 ? "post" : "posts"}</span>
        </div>
        <div className="stat-tile">
          <span className="stat-value">{likes}</span>
          <span className="stat-label">likes received</span>
        </div>
        <div className="stat-tile">
          <span className="stat-value">{comments}</span>
          <span className="stat-label">replies received</span>
        </div>
      </div>

      <h2>Your posts</h2>
      {posts.length === 0 ? (
        <p>
          You have not posted yet. Head to the{" "}
          <Link to="/community">community</Link> to write something.
        </p>
      ) : (
        <div className="post-feed">
          {posts.map((p) => (
            <div key={p._id} className="post-card">
              <div className="post-header">
                <span className="post-date">
                  {new Date(p.createdAt).toLocaleString()}
                </span>
              </div>
              <p className="post-content">{p.content}</p>
              <p className="chart-sub">
                {p.likes?.length || 0} likes · {p.comments?.length || 0} replies
              </p>
            </div>
          ))}
        </div>
      )}

      <p className="chart-sub">
        Posts can be deleted from the{" "}
        <Link to="/community">community page</Link>.
      </p>
    </div>
  );
};

export default Profile;
